import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client.ts';
import { queryDefaults } from './queries.ts';
import { useSession } from '../auth/session.tsx';
import type { CommissionPage, CommissionRow } from './types.ts';

export interface CommissionFilters {
  /** One state's clients. The headline counts do not move when this changes. */
  state?: string;
  county?: string;
  /** Clients whose kind needs no commission are hidden unless this says otherwise. */
  kinds?: 'show';
  sort: string;
}

function search(params: object): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') query.set(key, String(value));
  }
  return query.toString();
}

/**
 * The commission tracker.
 *
 * Every row on one scrolling page, as Billing is: no `limit` is sent, so the
 * server returns the whole set. The tracker is a few hundred rows — the
 * outstanding set is the part anyone reads, and it shrinks as the work is done.
 */
export function useCommissionTracker(filters: CommissionFilters) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['dashboards', 'commission', filters],
    queryFn: () =>
      apiFetch<CommissionPage>(`/dashboards/commission?${search(filters)}`, getToken),
    ...queryDefaults,
    placeholderData: (previous) => previous,
  });
}

/**
 * Move a client to a commission state.
 *
 * The whole tracker is invalidated rather than the row patched: a state change
 * moves the client between counts, between chips and — under the default sort —
 * to a different place in the list, because the age it is sorted on restarts
 * when the state does. Patching the row in place would leave every figure
 * around it stale.
 */
export function useSetCommissionState() {
  const { getToken } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      clientId,
      stateCode,
      blockedBy,
    }: {
      clientId: string;
      stateCode: string;
      blockedBy?: string;
    }) =>
      apiFetch<{ client: CommissionRow }>(`/commissions/${clientId}`, getToken, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          state_code: stateCode,
          ...(blockedBy ? { blocked_by: blockedBy } : {}),
        }),
      }),
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['dashboards', 'commission'] });
      void queryClient.invalidateQueries({ queryKey: ['client', variables.clientId] });
    },
  });
}
